import {useMemo} from 'react';
import {
  Conversation,
  ConversationLogItem,
  ConversationMessage,
  TextConversationMessage,
} from '@stripe-internal/conversations-client';
import {tokens, useTokenValue} from '@sail/ui';
import {MessageAddedRow} from '../Conversation/Log/MessageAddedRow';
import {ParticipantJoinedRow} from '../Conversation/Log/ParticipantJoinedRow';
import {ParticipantLeftRow} from '../Conversation/Log/ParticipantLeftRow';
import {ChatEndedRow} from '../Chat/Row/ChatEndedRow';
import {TranslationRow} from '../Chat/Row/TranslationRow';
import {MetaEventRow} from '../Conversation/Log/MetaEventRow';
import {GroupedMessagesRowV2} from '../Conversation/Log/Messages/GroupedMessagesRowV2';
import {ChatStartedRow} from '../Chat/Row/ChatStartedRow';
import {CustomComponent} from '../Conversation/Log/Messages/CustomComponent';
import {avatarForMessage} from '../Conversation/Log/Messages/avatarForMessage';
import {ChatBubbleNextConfig} from '../Chat/Row/ChatBubbleNext';
import {AvatarSpacer} from '../Chat/AvatarSpacer';
import {LoadingText} from '../Conversation/LoadingText';
import {BlinkingCursor} from '../Conversation/BlinkingCursor';
import {TypingIndicatorNext} from '../Conversation/TypingIndicatorNext';
import {useMessagesTopAnchored} from './useMessagesTopAnchored';

export type GroupingOptions = {
  useNewDesign?: boolean;
  useDotComDesign?: boolean;
  animated?: boolean;
  disableInteraction?: boolean;
  messageRenderer?: (msg: ConversationMessage) => JSX.Element | null;
  shouldAddLoadingIndicators?: boolean;
};

type GroupingState = {
  items: Array<JSX.Element>;
  isOwn: boolean;
  id: string | null | undefined;
};

// Define a type guard to check if a message is a TextConversationMessage
export function isTextConversationMessage(
  message: ConversationMessage,
): message is TextConversationMessage {
  return (
    (message as TextConversationMessage).type === 'text-conversation-message'
  );
}

export function isAssistantEntryPoint(conversation: Conversation) {
  return conversation.context.triggerParams?.triggerPoint?.startsWith(
    'assistant',
  );
}

function isOwn(
  conversation: Conversation,
  item: ConversationLogItem | null | undefined,
) {
  return (
    item?.name === 'conversation-message-added' &&
    item?.message?.participant.id === conversation.user.id
  );
}

function isResponseExpected(
  item: ConversationLogItem,
  conversation: Conversation,
) {
  if (item.name !== 'conversation-message-added') {
    return false;
  }

  const isFromUser = item.message.participant.id === conversation.user.id;

  const isTextOrSelectedOption =
    item.message.type === 'text-conversation-message' ||
    (item.message.type === 'button-options-conversation-message' &&
      item.message.selectedOptionId != null);

  return isFromUser && isTextOrSelectedOption;
}

function addLoadingIndicators(
  conversation: Conversation,
  item: ConversationLogItem,
  currentState: GroupingState,
  animated: boolean,
  shouldTopAnchor: boolean,
) {
  let loaderRow = null;

  // The typing indicator doesn't optionally use the
  // `GroupedMessagesRowV2` component because we need to control
  // the conditional rendering here, but the typing indicator
  // controls that state internally. That means we can't support
  // this type of loading indicator when scrolling up is enabled;
  // that's ok because this is only for talking to a human agent
  currentState.items.push(
    <TypingIndicatorNext
      key="typing-indicator"
      conversation={conversation}
      animated={animated}
    />,
  );

  // We conditionally show the blinking cursor indicator,
  // inferring that a response is coming from the message. We do
  // this declaratively so the loader is shown instantly after the
  // user message appears
  if (
    isResponseExpected(item, conversation) &&
    conversation.context.channel !== 'chat'
  ) {
    const loadingText = isAssistantEntryPoint(conversation) ? (
      <LoadingText shouldReset />
    ) : (
      <BlinkingCursor />
    );

    // If top anchoring, we want the cursor to always exist in a
    // new group to ensure that have the full height of the log to
    // scroll up with. Otherwise, we don't group it at all and it
    // exists as a normal item
    if (shouldTopAnchor) {
      loaderRow = (
        <GroupedMessagesRowV2
          key="grouped-loading"
          rows={[loadingText]}
          fillSpace={shouldTopAnchor}
        />
      );
    } else {
      currentState.items.push(loadingText);
    }
  }

  return loaderRow;
}

function shouldBreak(
  conversation: Conversation,
  item: ConversationLogItem,
  prevItem: ConversationLogItem | undefined,
  currentState: GroupingState,
  groupCache: Map<string, string>,
  shouldTopAnchor: boolean,
) {
  const expectedGroupId = groupCache.get(item.id);
  let isBreak = false;

  if (expectedGroupId) {
    isBreak = currentState.id !== expectedGroupId;
  } else if (shouldTopAnchor) {
    const isUniversalHelpMenu =
      item?.name === 'conversation-message-added' &&
      item?.message?.type === 'inline-data-conversation-message' &&
      item?.message?.inlineData?.name === 'universal_help_channel_menu';
    const isUnselectedOptions =
      item?.name === 'conversation-message-added' &&
      item?.message?.type === 'button-options-conversation-message' &&
      item?.message?.selectedOptionId == null;

    isBreak =
      isUniversalHelpMenu ||
      (isOwn(conversation, item) !== isOwn(conversation, prevItem) &&
        !isUnselectedOptions);
  }

  return isBreak;
}

export function useItemsGroupedV2(
  conversation: Conversation,
  items: ConversationLogItem[],
  {
    useNewDesign,
    useDotComDesign,
    animated,
    disableInteraction,
    messageRenderer,
    shouldAddLoadingIndicators,
  }: GroupingOptions,
) {
  const {hasTopAnchorFlag, shouldTopAnchor} =
    useMessagesTopAnchored(conversation);
  const xsmallBorder = useTokenValue(tokens.border.radius.xsmall);
  const groupCache = useMemo(() => new Map<string, string>(), []);

  if (!hasTopAnchorFlag) {
    return null;
  }

  const rows = [];

  let translationRowAdded = false;
  let conversationParticipantJoined = false;

  let currentState: GroupingState = {
    items: [],
    isOwn: items.length > 0 ? isOwn(conversation, items[0]) : false,
    id: items.length > 0 ? items[0].id : null,
  };

  const groupAndResetState = (item?: ConversationLogItem) => {
    if (currentState.items.length > 0) {
      rows.push(
        <GroupedMessagesRowV2
          key={`grouped-${currentState.id}`}
          rows={currentState.items}
          fillSpace={shouldTopAnchor && !currentState.isOwn}
        />,
      );
    }

    currentState = {
      items: [],
      isOwn: item ? isOwn(conversation, item) : false,
      id: item?.id,
    };
  };

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    const prevItem = items[i - 1] || null;
    const nextItem = items[i + 1] || null;

    if (
      shouldBreak(
        conversation,
        item,
        prevItem,
        currentState,
        groupCache,
        !!shouldTopAnchor,
      )
    ) {
      groupAndResetState(item);
    }

    if (!groupCache.has(item.id)) {
      groupCache.set(item.id, currentState.id!);
    }

    switch (item.name) {
      case 'conversation-participant-joined': {
        conversationParticipantJoined = true;
        currentState.items.push(
          <ParticipantJoinedRow
            key={item.id}
            conversation={conversation}
            item={item}
          />,
        );
        break;
      }
      case 'conversation-participant-left': {
        currentState.items.push(
          <ParticipantLeftRow
            key={item.id}
            conversation={conversation}
            item={item}
          />,
        );
        break;
      }
      case 'conversation-message-added': {
        const isTranslationCase =
          isTextConversationMessage(item.message) &&
          !!item.message.originalText;

        if (
          isTranslationCase &&
          !translationRowAdded &&
          // Only show this when an agent has joined. Without this
          // condition UH can trigger the row render.
          conversationParticipantJoined
        ) {
          currentState.items.push(<TranslationRow />);
          translationRowAdded = true;
        }

        const avatar = avatarForMessage(item);
        const spacer = avatar ? <AvatarSpacer /> : null;

        const isSameParticipant =
          nextItem?.name === 'conversation-message-added' &&
          item.message?.participant.id === nextItem?.message?.participant.id;
        const isAgent = item.message?.participant.role === 'agent';

        currentState.items.push(
          <ChatBubbleNextConfig.Customize
            css={
              // This logic adds CSS if this is the last message for
              // this participant to tighten the border radius. Which
              // corner we tighten depends if the message is on the
              // left or right (if own message or not)
              !isSameParticipant
                ? {
                    borderBottomRightRadius: !isAgent
                      ? xsmallBorder
                      : undefined,
                    borderBottomLeftRadius: isAgent ? xsmallBorder : undefined,
                  }
                : {}
            }
          >
            <MessageAddedRow
              conversation={conversation}
              key={`grouped-${item.id}`}
              item={item}
              animated={animated}
              avatar={
                prevItem?.name === 'conversation-message-added' &&
                prevItem?.message?.participant.id !==
                  item.message?.participant.id
                  ? avatar
                  : spacer
              }
              disableInteraction={disableInteraction}
              messageRenderer={messageRenderer}
            />
          </ChatBubbleNextConfig.Customize>,
        );

        break;
      }
      case 'conversation-meta': {
        currentState.items.push(<MetaEventRow key={item.id} item={item} />);
        break;
      }
      case 'conversation-started': {
        if (useNewDesign || useDotComDesign) {
          currentState.items.push(
            <ChatStartedRow key={item.id} conversation={conversation} />,
          );
        }
        break;
      }
      case 'conversation-custom-component': {
        currentState.items.push(<CustomComponent {...item} />);
        break;
      }
      case 'conversation-ended': {
        if (!useNewDesign && !conversation.context.surpressChatEndedNotice) {
          currentState.items.push(<ChatEndedRow key={item.id} />);
        }
        break;
      }
      default:
      // unknown log item
    }
  }

  let loaderRow = null;
  if (shouldAddLoadingIndicators) {
    // Once all the items are processed, we run logic to show any
    // necessary loading indicators. This logic may ask to add an
    // additional row to the list
    if (items.length > 0) {
      loaderRow = addLoadingIndicators(
        conversation,
        items[items.length - 1],
        currentState,
        !!animated,
        !!shouldTopAnchor,
      );
    }
  }

  // If we've reached the end, we always want to group the remaining
  // items in the state
  groupAndResetState();

  if (loaderRow && !useDotComDesign) {
    rows.push(loaderRow);
  }

  return rows;
}
